// Synthesizes MCO combo rows for the Add Plan endorsements step, in the same shape
// optimizeInsurance already produces, so InsuranceCalculatePremiumEndorsementsTableGrid
// renders them unchanged. MCO cannot go through optimizeInsurance itself — its plan
// names and math are not in the toolkit.

import {
  INSURANCE_PLAN_NAME_APH,
  INSURANCE_PLAN_NAME_ECO_RP,
  INSURANCE_PLAN_NAME_ECO_RP_HPE,
  INSURANCE_PLAN_NAME_ECO_YP,
  INSURANCE_PLAN_NAME_RP,
  INSURANCE_PLAN_NAME_RP_HPE,
  INSURANCE_PLAN_NAME_STAX_RP,
  INSURANCE_PLAN_NAME_STAX_RP_HPE,
  INSURANCE_PLAN_NAME_YP,
} from "@datanac/datanac-api-toolkit/dist/datanac-rma-api-helper";
import { calculate_cecl_from_stresstest } from "@datanac/datanac-api-toolkit/dist/insurance/stress-test/cecl";
import { MCOService } from "components/Insurance/MCOService";

import { loadMcoAreaBundle, loadMcoBandsFromAreaRate, MCO_PLAN_NAMES } from "./mcoAreaInputs.js";
import { computeMcoProjectedPayment, roundHalfUp } from "./mcoModel.js";
import { loadMcoActuarialAndCropLeg, loadMcoAreaLossBands, paymentBaseFromActuarial } from "./mcoOfficialData.js";
import { computeMcoProducerPremium } from "./mcoPremium.js";
import { cloneComboForMco, defaultMcoBandsForYear, num } from "./mcoShared.js";

const ECO_NAMES = [INSURANCE_PLAN_NAME_ECO_RP, INSURANCE_PLAN_NAME_ECO_RP_HPE, INSURANCE_PLAN_NAME_ECO_YP];
const STAX_NAMES = [INSURANCE_PLAN_NAME_STAX_RP, INSURANCE_PLAN_NAME_STAX_RP_HPE];

function comboPlans(combo) {
  return combo?.comboStressTestResults?.insurancePlans || [];
}

function mcoPlanCodeFor(basePlanName) {
  if (basePlanName === INSURANCE_PLAN_NAME_YP || basePlanName === INSURANCE_PLAN_NAME_APH) return "67";
  if (basePlanName === INSURANCE_PLAN_NAME_RP_HPE) return "69";
  if (basePlanName === INSURANCE_PLAN_NAME_RP) return "68";
  return null; // ARPI / standalone STAX / unrecognized base plan — MCO cannot attach
}

/**
 * @param {object} opts
 * @param {object[]} opts.baseCombos — optimizeInsurance() output for this Endorsements step
 * @param {object} opts.farmParameters — reported_acreage, approved_yield, share_percent, state/county/commodity/type/practice names
 * @param {object} opts.countyParameters — reinsurance_year, projected_price
 * @param {string} opts.basePlanName — selectedInsuranceOfferCrop.insurance_plan_name
 * @returns {Promise<object[]>} MCO combo rows, same shape as baseCombos
 */
export async function buildMcoComboRows({ baseCombos, farmParameters, countyParameters, basePlanName }) {
  const planCode = mcoPlanCodeFor(basePlanName);
  if (!planCode || !baseCombos?.length) return [];

  // Prefer countyData.reinsurance_year; budget uses `year` as the crop/reinsurance year.
  const reinsuranceYear = String(
    countyParameters?.reinsurance_year || farmParameters?.reinsurance_year || farmParameters?.year || ""
  );
  const geo = {
    reinsurance_year: reinsuranceYear,
    crop_year: reinsuranceYear,
    state_name: farmParameters?.state_name || farmParameters?.location_state_name,
    county_name: farmParameters?.county_name || farmParameters?.location_county_name,
    commodity_name: farmParameters?.commodity_name,
    type_name: farmParameters?.type_name,
    practice_name: farmParameters?.practice_name,
  };
  if (!geo.crop_year || !geo.state_name || !geo.county_name || !geo.commodity_name) {
    console.warn("buildMcoComboRows: missing geo", geo);
    return [];
  }

  const crop = {
    stateName: geo.state_name,
    countyName: geo.county_name,
    commodityName: geo.commodity_name,
    type_name: geo.type_name,
    practice_name: geo.practice_name,
    acres: num(farmParameters?.reported_acreage),
    yieldPerAcre: num(farmParameters?.approved_yield),
    sharePct: farmParameters?.share_percent,
    price: countyParameters?.projected_price,
  };

  // Prefer ADM AreaRate bands (names only). Official AreaLoss is optional fallback.
  let bands = [];
  try {
    bands = await loadMcoBandsFromAreaRate(crop, planCode, geo.crop_year);
  } catch (e) {
    console.warn("loadMcoBandsFromAreaRate", e);
  }
  if (!bands.length) {
    try {
      bands = await loadMcoAreaLossBands(MCOService().actions, geo);
    } catch (e) {
      console.warn("loadMcoAreaLossBands", e);
    }
  }
  if (!bands.length) {
    bands = defaultMcoBandsForYear(reinsuranceYear);
  }
  if (!bands.length) {
    console.warn("buildMcoComboRows: no MCO bands for", geo);
    return [];
  }

  const eligibleCombos = baseCombos.filter(
    (combo) => !comboPlans(combo).some((p) => ECO_NAMES.includes(p.insurance_plan_name))
  );
  if (!eligibleCombos.length) {
    console.warn("buildMcoComboRows: no non-ECO combos to attach MCO to");
    return [];
  }

  const acres = crop.acres;
  const approvedYield = crop.yieldPerAcre;
  if (!(acres > 0) || !(approvedYield > 0)) {
    console.warn("buildMcoComboRows: acres/yield missing", { acres, approvedYield });
    return [];
  }

  const mcoService = MCOService().actions;
  const rows = [];

  for (const combo of eligibleCombos) {
    const staxCoverage = comboPlans(combo).find((p) => STAX_NAMES.includes(p.insurance_plan_name))
      ?.insurancePlanParameters?.coverage_level_percent;
    const allowedBands = staxCoverage === 0.9 ? bands.filter((b) => b.start === 0.95) : bands;

    for (const band of allowedBands) {
      try {
        const mcoRow = await buildOneMcoRow({
          combo,
          band,
          planCode,
          crop,
          geo,
          acres,
          approvedYield,
          mcoService,
        });
        if (mcoRow) rows.push(mcoRow);
      } catch (e) {
        console.warn("buildOneMcoRow", band, e);
      }
    }
  }

  if (!rows.length) console.warn("buildMcoComboRows: bands found but every row failed", bands);
  return rows;
}

async function buildOneMcoRow({ combo, band, planCode, crop, geo, acres, approvedYield, mcoService }) {
  const areaBundle = await loadMcoAreaBundle(
    crop,
    { planCode, coverageStart: band.start, areaLossEnd: band.end, protectionFactor: 1 },
    geo.crop_year
  );
  if (!areaBundle.ready) {
    console.warn("loadMcoAreaBundle", areaBundle.blockers);
    return null;
  }

  // Official Watts cascade needs the ADM-resolved type (often "No Type Specified"), not Grain.
  const typeName = areaBundle.bundle.typeName || geo.type_name;

  // Best-effort: Official actuarial drives the per-cell indemnity, and its crop leg carries
  // MCO's own projected price. Premium still attaches if Official fails.
  let paymentBase = null;
  try {
    const common = {
      crop_year: geo.crop_year,
      state_name: geo.state_name,
      county_name: geo.county_name,
      commodity_name: geo.commodity_name,
      type_name: typeName,
      practice_name: geo.practice_name,
      insurance_plan_code: planCode,
      approved_yield: approvedYield,
      area_loss_order: band.order,
      area_loss_start_percent: band.start,
      area_loss_end_percent: band.end,
      protection_factor: 1,
    };
    const { actuarial, cropLeg } = await loadMcoActuarialAndCropLeg(mcoService, common);
    paymentBase = paymentBaseFromActuarial(actuarial, cropLeg, {
      approvedYield,
      planCode,
      coverageStart: band.start,
      areaLossEnd: band.end,
      protectionFactor: 1,
      sharePct: crop.sharePct,
      acres,
    });
  } catch (e) {
    console.warn("Official MCO actuarial skipped", e);
  }

  // Premium is billed at the September sales close, so protection is sized on MCO's own
  // projected price (Official crop leg) — never the base plan's county price, which is a
  // different discovery and drifts once a harvest price exists.
  const mcoProjectedPrice = num(paymentBase?.projectedPrice);
  const premium = computeMcoProducerPremium(
    mcoProjectedPrice > 0 ? { ...areaBundle.bundle, projectedPrice: mcoProjectedPrice } : areaBundle.bundle
  );
  if (!premium.ready) return null;
  const producerPremiumPerAcre = premium.result.producerPremiumAmount / acres;

  const mcoCombo = cloneComboForMco(combo);
  const cells = (mcoCombo.comboStressTestResults?.results || []).flat();
  if (!cells.length) return null;

  for (const cell of cells) {
    if (paymentBase) {
      const { ready, result } = computeMcoProjectedPayment({
        ...paymentBase,
        finalYield: cell.county_yield,
        harvestPrice: cell.price,
      });
      // ponytail: MCO premium is folded into premium_cost_per_acre_total (feeds CECL/breakeven)
      // but not re-subtracted from this cell's already-computed net_profit_insured — matches
      // the plan's explicit Phase 3 spec. Upgrade path: rerun the toolkit's revenue_insured
      // formula per cell if MCO needs to show up in the per-cell P&L too.
      if (ready) cell.net_profit_insured = roundHalfUp(cell.net_profit_insured + result.mcoPayment, 2);
    }
    cell.premium_cost_per_acre_total = num(cell.premium_cost_per_acre_total) + producerPremiumPerAcre;
  }

  mcoCombo.premium_cost_total_per_acre = num(mcoCombo.premium_cost_total_per_acre) + producerPremiumPerAcre;
  mcoCombo.loss_amount_maximum_per_acre = Math.min(...cells.map((c) => c.net_profit_insured));

  const cecl = calculate_cecl_from_stresstest({ stressTestResults: mcoCombo.comboStressTestResults.results });
  mcoCombo.breakeven_projected = cecl?.loan_amount_insured ? cecl.loan_amount_insured / (approvedYield * acres) : null;

  const liabilityAmount = premium.result.liabilityAmount;
  mcoCombo.comboStressTestResults.insurancePlans = [
    ...mcoCombo.comboStressTestResults.insurancePlans,
    {
      insurance_plan_name: MCO_PLAN_NAMES[planCode][0],
      insurance_plan_code: planCode,
      coverage_level_percent: band.start,
      insurancePlanParameters: {
        coverage_level_percent: band.start,
        // Synthesized rows skip the toolkit state helper, so the farm parameters every other
        // plan carries have to be set here or they persist as NULL and the card cannot
        // render a per-acre figure.
        area_loss_end_percent: band.end,
        reported_acreage: acres,
        approved_yield: approvedYield,
        projected_price: mcoProjectedPrice > 0 ? mcoProjectedPrice : areaBundle.bundle.projectedPrice,
        insured_share_percent: areaBundle.bundle.insuredShareDecimal,
        liability_amount: liabilityAmount,
        liability_amount_per_acre: liabilityAmount / acres,
        producer_premium_amount: premium.result.producerPremiumAmount,
        producer_premium_amount_per_acre: producerPremiumPerAcre,
      },
    },
  ];

  return mcoCombo;
}
